import React, { useEffect, useState } from 'react';
import type { FC, MouseEvent } from 'react';
import type { MessageDescriptor } from 'react-intl';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';

import Link from '@atlaskit/link/link';
import Button from '@atlaskit/button/button';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import { token } from '@atlaskit/tokens';
import { useFlags } from '@atlaskit/flag/flag-provider';
import StatusSuccessIcon from '@atlaskit/icon/core/status-success';
import FeedbackCollector from '@atlaskit/feedback-collector/FeedbackCollector';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import Box from '@atlaskit/primitives/box';
import Inline from '@atlaskit/primitives/inline';
import { xcss } from '@atlaskit/primitives/xcss';

import { useAIEventsInstrumentation } from '@atlassian/ai-analytics/use-ai-events-instrumentation';

import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import { ThumbsIcon } from '../assets/ThumbIcon.tsx';
import { useCurrentUserContributor } from '../hooks/useCurrentUserContributor.tsx';

import type { SummaryAnalyticsPropertiesType } from './summarySharedTypes.ts';
import { useFeedbackCollectorProperties } from './FeedbackCollectorPropertiesProvider.tsx';

const LINK_TO_PRIVACY_POLICY = 'https://www.atlassian.com/legal/privacy-policy';
const LINK_TO_RESEARCH_GROUP = 'https://www.atlassian.com/research-group';

const i18n = defineMessages({
	flagTitle: {
		id: 'quick-summary.feedback.flag.title',
		defaultMessage: 'Thanks!',
		description:
			'Success flag title shown after submitting feedback on AI-generated content, confirming feedback received.',
	},
	flagDescription: {
		id: 'quick-summary.feedback.flag.description',
		defaultMessage: 'We appreciate you taking the time to send this feedback.',
		description:
			'Success flag description shown after submitting feedback on AI-generated content, thanking the user.',
	},
	likeTooltip: {
		id: 'quick-summary.like.tooltip',
		defaultMessage: 'Good response',
		description:
			'Tooltip on thumbs-up button next to AI-generated content. Indicates the response was helpful or accurate.',
	},
	dislikeTooltip: {
		id: 'quick-summary.dislike.tooltip',
		defaultMessage: 'Bad response',
		description:
			'Tooltip on thumbs-down button next to AI-generated content. Indicates the response was unhelpful.',
	},
	likeSuggestionTooltip: {
		id: 'quick-summary.like-suggestion.tooltip',
		defaultMessage: 'Helpful',
		description:
			'Tooltip on thumbs-up button next to AI-generated content. Indicates the suggestions were helpful or accurate.',
	},
	dislikeSuggestionTooltip: {
		id: 'quick-summary.dislike-suggestion.tooltip',
		defaultMessage: 'Not helpful',
		description:
			'Tooltip on thumbs-down button next to AI-generated content. Indicates the suggestions were unhelpful.',
	},
	feedbackTitle: {
		id: 'quick-summary.feedback.title',
		defaultMessage: 'Help us improve Atlassian Intelligence',
		description: 'Label text for the summary section of the report feedback collector',
	},
	feedbackSummaryTitle: {
		id: 'quick-summary.feedback.summary.title',
		defaultMessage: 'Tell us about your experience',
		description: 'Title text for the summary section of the report feedback collector',
	},
	feedbackSummaryPlaceholder: {
		id: 'quick-summary.feedback.summary.placeholder',
		defaultMessage: 'What did Atlassian Intelligence do well? What could be improved?',
		description: 'Description text for the summary section of the report feedback collector',
	},
	canContactText: {
		id: 'quick-summary.feedback.can-contact.label',
		defaultMessage: 'Atlassian can contact me about this feedback. See our {privacyPolicyLink}.',
		description:
			"Text next to first checkbox. {privacyPolicyLink} will be substituted with a link whose text reads 'privacy policy'",
	},
	privacyPolicyLink: {
		id: 'quick-summary.feedback.privacy-policy.link',
		defaultMessage: 'privacy policy',
		description: 'The text of the link that will direct users to the privacy policy site.',
	},
	improveAtlassianText: {
		id: 'quick-summary.feedback.improve-atlassian',
		defaultMessage:
			'I’d like to help improve Atlassian products by joining the {researchGroupLink}.',
		description:
			"Text next to second checkbox. {researchGroupLink} will be substituted with a message which reads as 'Atlassian Research Group'",
	},
	researchGroupLink: {
		id: 'quick-summary.feedback.research-group.link',
		defaultMessage: 'Atlassian Research Group',
		description: 'The text of the link that will direct users to the research group site.',
	},
});

type LinkType = {
	link: string;
};

const PrivacyPolicyLink = ({ link }: LinkType) => (
	<Link target="_blank" href={link}>
		<FormattedMessage {...i18n.privacyPolicyLink} />
	</Link>
);

const ResearchGroupLink = ({ link }: LinkType) => (
	<Link target="_blank" href={link}>
		<FormattedMessage {...i18n.researchGroupLink} />
	</Link>
);

const CanContactLabel = () => {
	return (
		<FormattedMessage
			{...i18n.canContactText}
			values={{
				privacyPolicyLink: <PrivacyPolicyLink link={LINK_TO_PRIVACY_POLICY} />,
			}}
		/>
	);
};

const EnrolLabel = () => {
	return (
		<FormattedMessage
			{...i18n.improveAtlassianText}
			values={{
				researchGroupLink: <ResearchGroupLink link={LINK_TO_RESEARCH_GROUP} />,
			}}
		/>
	);
};

const TooltipWrapperXCSS = xcss({
	listStyle: 'none',
	marginTop: 'space.0',
});

const FeedbackIconXCSS = xcss({
	marginRight: 'space.050',
});

const DislikeIconWrapperXCSS = xcss({
	transform: 'rotate(180deg)',
});

const ButtonWrapperXCSS = xcss({
	height: '24px',
	width: '24px',
});

const InlineULXCSS = xcss({
	paddingLeft: 'space.0',
	display: 'flex',
});

type Sentiment = 'good' | 'bad' | '';

type SmartsFeedbackComponentBaseProps = {
	analyticsProperties: SummaryAnalyticsPropertiesType;
	summaryId?: string;
	summaryLocale?: string;
	feedbackTitle?: MessageDescriptor;
	feedbackSummaryPlaceholder?: MessageDescriptor;
	aiContentType?: 'response' | 'suggestion';
};

// Ensures at least one of contentId or spaceKey is provided
export type SmartsFeedbackComponentProps = SmartsFeedbackComponentBaseProps &
	({ contentId: string; spaceKey?: string } | { contentId?: string; spaceKey: string });

export const SmartsFeedbackComponent: FC<SmartsFeedbackComponentProps> = ({
	analyticsProperties,
	contentId,
	spaceKey,
	summaryId,
	summaryLocale,
	feedbackTitle,
	feedbackSummaryPlaceholder,
	aiContentType,
}) => {
	const { showFlag } = useFlags();
	const { formatMessage } = useIntl();
	const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const feedbackCollectorProperties = useFeedbackCollectorProperties();
	const [sentiment, setSentiment] = useState<Sentiment>('');
	const [hasGoodFeedbackBeenSubmitted, setHasGoodFeedbackBeenSubmitted] = useState(false);
	const [hasBadFeedbackBeenSubmitted, setHasBadFeedbackBeenSubmitted] = useState(false);
	const { cloudId } = useSessionData();
	const { trackAIFeedbackSubmit } = useAIEventsInstrumentation();

	// Only fetch contributor info when contentId is provided (not for space-level feedback)
	const shouldFetchContributors = !!contentId;
	const contributorResult = useCurrentUserContributor(contentId || '');
	const { isCurrentUserContributor, contentContributorsError } = shouldFetchContributors
		? contributorResult
		: { isCurrentUserContributor: undefined, contentContributorsError: undefined };

	const feedbackContextParts = [
		contentId && `contentId: ${contentId}`,
		spaceKey && `spaceKey: ${spaceKey}`,
		`sentiment: ${sentiment}`,
		`cloudId: ${cloudId}`,
		`isCurrentUserContributor: ${isCurrentUserContributor}`,
		feedbackCollectorProperties.feedbackContext.length > 0 &&
			feedbackCollectorProperties.feedbackContext,
	].filter(Boolean);

	const feedbackContextString = feedbackContextParts.join(' ');

	useEffect(() => {
		if (isFeedbackOpen) {
			createAnalyticsEvent({
				type: 'sendScreenEvent',
				data: {
					name: `${analyticsProperties.source}FeedbackCollectorDialog`,
					attributes: {
						...(contentId && { contentId }),
						...(spaceKey && { spaceKey }),
						summaryId,
						summaryLocale,
						sentiment,
						...analyticsProperties.additionalAnalyticsAttributes,
					},
				},
			}).fire();
		}
	}, [
		createAnalyticsEvent,
		isFeedbackOpen,
		contentId,
		spaceKey,
		summaryId,
		summaryLocale,
		sentiment,
		analyticsProperties,
	]);

	const showFeedbackFlag = () => {
		showFlag({
			title: formatMessage(i18n.flagTitle),
			description: formatMessage(i18n.flagDescription),
			icon: <StatusSuccessIcon color={token('color.icon.success')} label="success" />,
			isAutoDismiss: true,
		});
	};

	const onLikeClicked = () => {
		setIsFeedbackOpen(true);
		setSentiment('good');

		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: analyticsProperties.source,
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: `${analyticsProperties.source}LikeButton`,
				attributes: {
					...(contentId && { contentId }),
					...(spaceKey && { spaceKey }),
					summaryId,
					summaryLocale,
					...analyticsProperties.additionalAnalyticsAttributes,
				},
			},
		}).fire();

		trackAIFeedbackSubmit('up');
	};

	const onDislikeClicked = () => {
		setIsFeedbackOpen(true);
		setSentiment('bad');
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: analyticsProperties.source,
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: `${analyticsProperties.source}DislikeButton`,
				attributes: {
					...(contentId && { contentId }),
					...(spaceKey && { spaceKey }),
					summaryId,
					summaryLocale,
					...analyticsProperties.additionalAnalyticsAttributes,
				},
			},
		}).fire();
		trackAIFeedbackSubmit('down');
	};

	const onFeedbackSubmit = () => {
		showFeedbackFlag();
		setIsFeedbackOpen(false);

		if (sentiment === 'good') {
			setHasGoodFeedbackBeenSubmitted(true);
			setHasBadFeedbackBeenSubmitted(false);
		} else {
			setHasBadFeedbackBeenSubmitted(true);
			setHasGoodFeedbackBeenSubmitted(false);
		}

		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: analyticsProperties.source,
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: `${analyticsProperties.source}SubmitFeedbackButton`,
				attributes: {
					...(contentId && { contentId }),
					...(spaceKey && { spaceKey }),
					summaryId,
					summaryLocale,
					sentiment,
					...analyticsProperties.additionalAnalyticsAttributes,
				},
			},
		}).fire();
	};

	const likeTooltip =
		aiContentType && aiContentType === 'suggestion' ? i18n.likeSuggestionTooltip : i18n.likeTooltip;
	const dislikeTooltip =
		aiContentType && aiContentType === 'suggestion'
			? i18n.dislikeSuggestionTooltip
			: i18n.dislikeTooltip;

	return (
		<Box role="none" onClick={(e: MouseEvent<HTMLElement>) => e.stopPropagation()}>
			<Inline alignBlock="center" space="space.050">
				<Box as="ul" xcss={InlineULXCSS}>
					<Box as="li" xcss={[TooltipWrapperXCSS, FeedbackIconXCSS]}>
						<Tooltip hideTooltipOnMouseDown content={formatMessage(likeTooltip)} position="top">
							<Box xcss={ButtonWrapperXCSS}>
								<Button
									appearance="subtle"
									aria-label={formatMessage(likeTooltip)}
									spacing="none"
									iconBefore={<ThumbsIcon isSelected={hasGoodFeedbackBeenSubmitted} />}
									onClick={onLikeClicked}
									testId="feedback-like-button"
									isDisabled={hasGoodFeedbackBeenSubmitted}
								/>
							</Box>
						</Tooltip>
					</Box>
					<Box as="li" xcss={[TooltipWrapperXCSS, FeedbackIconXCSS]}>
						<Tooltip hideTooltipOnMouseDown content={formatMessage(dislikeTooltip)} position="top">
							<Box xcss={ButtonWrapperXCSS}>
								<Button
									appearance="subtle"
									aria-label={formatMessage(dislikeTooltip)}
									spacing="none"
									iconBefore={
										<Box xcss={DislikeIconWrapperXCSS}>
											<ThumbsIcon isSelected={hasBadFeedbackBeenSubmitted} />
										</Box>
									}
									onClick={onDislikeClicked}
									testId="feedback-dislike-button"
									isDisabled={hasBadFeedbackBeenSubmitted}
								/>
							</Box>
						</Tooltip>
					</Box>
				</Box>
				{isFeedbackOpen && (
					<FeedbackCollector
						locale={feedbackCollectorProperties.locale}
						onSubmit={onFeedbackSubmit}
						onClose={() => setIsFeedbackOpen(false)}
						atlassianAccountId={feedbackCollectorProperties.userId}
						name={feedbackCollectorProperties.displayName}
						entrypointId={feedbackCollectorProperties.entrypointId}
						summaryPlaceholder={formatMessage(
							feedbackSummaryPlaceholder ?? i18n.feedbackSummaryPlaceholder,
						)}
						additionalFields={[
							{
								id: feedbackCollectorProperties.feedbackContextId,
								value: feedbackContextString,
							},
						]}
						feedbackTitle={formatMessage(feedbackTitle ?? i18n.feedbackTitle)}
						showTypeField={false}
						customTextAreaLabel={formatMessage(i18n.feedbackSummaryTitle)}
						canBeContactedLabel={<CanContactLabel />}
						enrolInResearchLabel={<EnrolLabel />}
					/>
				)}
			</Inline>
			{contentContributorsError && <ErrorDisplay error={contentContributorsError} />}
		</Box>
	);
};
